import styled from "@emotion/styled";
import { ErrorMessage } from "@hookform/error-message";
import { Stack, useMediaQuery, useTheme } from "@mui/material";
import { EthiopianDatePicker, Upload } from "components/UI";
import {
  ADDRESSES,
  useCities,
  useCountries,
  useRegions,
  useSubcities,
  useWoredas,
  useZones
} from "features/members";
import { MuiTelInput } from "mui-tel-input";
import { useEffect } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Label = styled.label`
  color: #5b5b5b;
  font-size: 12px;
  font-weight: 500;
`;

const Input = styled.input`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
`;

const Select = styled.select`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
  font-family: inherit;
`;

const HelperText = styled.span`
  font-size: 12px;
  color: ${({ theme, error }) =>
    error ? theme.palette.error.main : "inherit"};
`;

export const PersonalInfoForm = ({
  register,
  errors,
  control,
  setValue,
  watch
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const countriesQuery = useCountries();
  const regionsQuery = useRegions({
    countryId: watch("birth_country_id")
  });
  const zonesQuery = useZones({
    regionId: watch("birth_region_id")
  });
  const citiesQuery = useCities({
    zoneId: watch("birth_zone_id")
  });
  const subcitiesQuery = useSubcities({
    cityId: watch("birth_city_id")
  });
  const woredasQuery = useWoredas({
    subcityId: watch("birth_sub_city_id")
  });

  useEffect(() => {
    if (countriesQuery.data) {
      setValue("nationality_id", "66");
      setValue("birth_country_id", "66");
    }
  }, [countriesQuery.data]);

  const isOutsideEthiopia = watch("birth_country_id") !== "66";

  return (
    <Stack spacing={2}>
      <Field>
        <Label htmlFor="employee_id">{t("HR.PersonalInfo.Employee ID")}</Label>
        <Input id="employee_id" {...register("employee_id")} />
        <ErrorMessage
          errors={errors}
          name="employee_id"
          render={({ message }) => <HelperText error>{message}</HelperText>}
        />
      </Field>

      {/* First row: Title and First Name */}
      <Stack direction={isMobile ? "column" : "row"} spacing={2}>
        <Field>
          <Label htmlFor="title">{t("HR.PersonalInfo.Salutation")}</Label>
          <Input id="title" {...register("title")} />
          <ErrorMessage
            errors={errors}
            name="title"
            render={({ message }) => <HelperText error>{message}</HelperText>}
          />
        </Field>
        <Field>
          <Label htmlFor="first_name">{t("HR.PersonalInfo.FirstName")}</Label>
          <Input id="first_name" {...register("first_name")} />
          <ErrorMessage
            errors={errors}
            name="first_name"
            render={({ message }) => <HelperText error>{message}</HelperText>}
          />
        </Field>
      </Stack>

      {/* Second row: Middle Name and Last Name */}
      <Stack direction={isMobile ? "column" : "row"} spacing={2}>
        <Field>
          <Label htmlFor="middle_name">{t("HR.PersonalInfo.MiddleName")}</Label>
          <Input id="middle_name" {...register("middle_name")} />
          <ErrorMessage
            errors={errors}
            name="middle_name"
            render={({ message }) => <HelperText error>{message}</HelperText>}
          />
        </Field>
        <Field>
          <Label htmlFor="last_name">{t("HR.PersonalInfo.LastName")}</Label>
          <Input id="last_name" {...register("last_name")} />
          <ErrorMessage
            errors={errors}
            name="last_name"
            render={({ message }) => <HelperText error>{message}</HelperText>}
          />
        </Field>
      </Stack>

      <Field>
        <Label htmlFor="mother_name">{t("HR.AddEmployee.MotherName")}</Label>
        <Input id="mother_name" {...register("mother_name")} />
        <ErrorMessage
          errors={errors}
          name="mother_name"
          render={({ message }) => <HelperText error>{message}</HelperText>}
        />
      </Field>

      <Field>
        <Label htmlFor="gender">{t("form.common.gender")}</Label>
        <Select id="gender" {...register("gender")}>
          <option value="FEMALE">{t("form.common.female")}</option>
          <option value="MALE">{t("form.common.male")}</option>
        </Select>
        <ErrorMessage
          errors={errors}
          name="gender"
          render={({ message }) => <HelperText error>{message}</HelperText>}
        />
      </Field>
      <Field>
        <Label htmlFor="date_of_birth">
          {t("HR.PersonalInfo.DateOfBirth")}
        </Label>
        <EthiopianDatePicker
          value={watch("date_of_birth")}
          onChange={e => setValue("date_of_birth", e.target.value)}
        />
        <ErrorMessage
          errors={errors}
          name="date_of_birth"
          render={({ message }) => <HelperText error>{message}</HelperText>}
        />
      </Field>

      <Field>
        <Label htmlFor="birth_country_id">
          {t("addMembers.step1.BirthCountry")}
        </Label>
        <Select
          id="birth_country_id"
          {...register("birth_country_id")}
          onChange={e => {
            setValue("birth_country_id", e.target.value, {
              shouldValidate: true
            });
            setValue("birth_region_id", "", {
              shouldValidate: true
            });
            setValue("birth_zone_id", "", {
              shouldValidate: true
            });
            setValue("birth_city_id", "", {
              shouldValidate: true
            });
            setValue("birth_sub_city_id", "", {
              shouldValidate: true
            });
            setValue("birth_woreda_id", "", {
              shouldValidate: true
            });
          }}
        >
          <option value="" disabled>
            Select country
          </option>
          {countriesQuery.data?.result?.map(country => (
            <option key={country.id} value={country.id}>
              {country.name}
            </option>
          ))}
        </Select>
        <ErrorMessage
          errors={errors}
          name="birth_country_id"
          render={({ message }) => <HelperText error>{message}</HelperText>}
        />
      </Field>

      {!isOutsideEthiopia && (
        <>
          <Field>
            <Label htmlFor="birth_region_id">
              {t("addMembers.step1.BirthRegion")}
            </Label>
            <Select
              id="birth_region_id"
              {...register("birth_region_id")}
              onChange={e => {
                setValue("birth_region_id", e.target.value, {
                  shouldValidate: true
                });

                if (e.target.value === ADDRESSES.ADDIS_ABABA.REGION_ID) {
                  setValue("birth_zone_id", ADDRESSES.ADDIS_ABABA.ZONE_ID, {
                    shouldValidate: true
                  });
                  setValue("birth_city_id", ADDRESSES.ADDIS_ABABA.CITY_ID, {
                    shouldValidate: true
                  });
                } else {
                  setValue("birth_zone_id", "", {
                    shouldValidate: true
                  });
                  setValue("birth_city_id", "", {
                    shouldValidate: true
                  });
                  setValue("birth_sub_city_id", "", {
                    shouldValidate: true
                  });
                  setValue("birth_woreda_id", "", {
                    shouldValidate: true
                  });
                }
              }}
            >
              <option value="" disabled>
                Select region
              </option>
              {regionsQuery.data?.result?.map(region => (
                <option key={region.id} value={region.id}>
                  {region.name}
                </option>
              ))}
            </Select>
            <ErrorMessage
              errors={errors}
              name="birth_region_id"
              render={({ message }) => <HelperText error>{message}</HelperText>}
            />
          </Field>

          {watch("birth_region_id") !== ADDRESSES.ADDIS_ABABA.REGION_ID && (
            <>
              <Field>
                <Label htmlFor="birth_zone_id">
                  {t("addMembers.step1.BirthZone")}
                </Label>
                <Select
                  id="birth_zone_id"
                  {...register("birth_zone_id")}
                  onChange={e => {
                    setValue("birth_zone_id", e.target.value, {
                      shouldValidate: true
                    });
                    setValue("birth_city_id", "", {
                      shouldValidate: true
                    });
                    setValue("birth_sub_city_id", "", {
                      shouldValidate: true
                    });
                    setValue("birth_woreda_id", "", {
                      shouldValidate: true
                    });
                  }}
                >
                  <option value="" disabled>
                    Select zone
                  </option>
                  {zonesQuery.data?.result?.map(zone => (
                    <option key={zone.id} value={zone.id}>
                      {zone.name}
                    </option>
                  ))}
                </Select>
                <ErrorMessage
                  errors={errors}
                  name="birth_zone_id"
                  render={({ message }) => (
                    <HelperText error>{message}</HelperText>
                  )}
                />
              </Field>

              <Field>
                <Label htmlFor="birth_city_id">
                  {t("addMembers.step1.BirthCity")}
                </Label>
                <Select
                  id="birth_city_id"
                  {...register("birth_city_id")}
                  onChange={e => {
                    setValue("birth_city_id", e.target.value, {
                      shouldValidate: true
                    });
                    setValue("birth_sub_city_id", "", {
                      shouldValidate: true
                    });
                    setValue("birth_woreda_id", "", {
                      shouldValidate: true
                    });
                  }}
                >
                  <option value="" disabled>
                    Select city
                  </option>
                  {citiesQuery.data?.result?.map(city => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
                </Select>
                <ErrorMessage
                  errors={errors}
                  name="birth_city_id"
                  render={({ message }) => (
                    <HelperText error>{message}</HelperText>
                  )}
                />
              </Field>
            </>
          )}

          <Field>
            <Label htmlFor="birth_sub_city_id">
              {t("addMembers.step1.BirthSubcity")}
            </Label>
            <Select
              id="birth_sub_city_id"
              {...register("birth_sub_city_id")}
              onChange={e => {
                setValue("birth_sub_city_id", e.target.value, {
                  shouldValidate: true
                });
                setValue("birth_woreda_id", "", {
                  shouldValidate: true
                });
              }}
            >
              <option value="" disabled>
                Select subcity
              </option>
              {subcitiesQuery.data?.result?.map(subcity => (
                <option key={subcity.id} value={subcity.id}>
                  {subcity.name}
                </option>
              ))}
            </Select>
            <ErrorMessage
              errors={errors}
              name="birth_sub_city_id"
              render={({ message }) => <HelperText error>{message}</HelperText>}
            />
          </Field>

          <Field>
            <Label htmlFor="birth_woreda_id">
              {t("addMembers.step1.BirthWoreda")}
            </Label>
            <Select
              id="birth_woreda_id"
              {...register("birth_woreda_id")}
              onChange={e => {
                setValue("birth_woreda_id", e.target.value, {
                  shouldValidate: true
                });
              }}
            >
              <option value="" disabled>
                Select woreda
              </option>
              {woredasQuery.data?.result?.map(woreda => (
                <option key={woreda.id} value={woreda.id}>
                  {woreda.name}
                </option>
              ))}
            </Select>
            <ErrorMessage
              errors={errors}
              name="birth_woreda_id"
              render={({ message }) => <HelperText error>{message}</HelperText>}
            />
          </Field>
        </>
      )}

      <Field>
        <Label htmlFor="nationality_id">
          {t("HR.PersonalInfo.Nationality")}
        </Label>
        <Select id="nationality_id" {...register("nationality_id")}>
          {countriesQuery.data?.result?.map(country => (
            <option key={country.id} value={country.id}>
              {country.nationality}
            </option>
          ))}
        </Select>
        <ErrorMessage
          errors={errors}
          name="nationality_id"
          render={({ message }) => <HelperText error>{message}</HelperText>}
        />
      </Field>
      <Controller
        name="employee_photo"
        control={control}
        render={({ field }) => (
          <Upload
            title={t("addMembers.step1.UploadPhoto")}
            buttonTitle={t("ui.dropzone.browse")}
            error={!!errors.employee_photo?.message}
            helperText={errors.employee_photo?.message}
            onChange={field.onChange}
          />
        )}
      />
      <Field>
        <Label htmlFor="phone_number">{t("HR.Adress.Phone Number")}</Label>
        <Controller
          name="phone_number"
          control={control}
          render={({ field }) => (
            <MuiTelInput
              forceCallingCode
              defaultCountry="ET"
              disableFormatting
              size="small"
              inputProps={{
                maxLength: 13
              }}
              {...field}
              sx={{
                "& .MuiInputBase-root": {
                  backgroundColor: "rgb(255, 255, 255)"
                },
                "& fieldset": {
                  borderWidth: "0.73px",
                  borderColor: "#b3b3b3"
                }
              }}
            />
          )}
        />
        <ErrorMessage
          errors={errors}
          name="phone_number"
          render={({ message }) => <HelperText error>{message}</HelperText>}
        />
      </Field>

      <Field>
        <Label htmlFor="email">{t("HR.Adress.Email")}</Label>
        <Input id="email" {...register("email")} />
        <ErrorMessage
          errors={errors}
          name="email"
          render={({ message }) => <HelperText error>{message}</HelperText>}
        />
      </Field>
      <Field>
        <Label htmlFor="address">{t("HR.Adress.Residential Address")}</Label>
        <Input id="address" {...register("address")} />
        <ErrorMessage
          errors={errors}
          name="address"
          render={({ message }) => <HelperText error>{message}</HelperText>}
        />
      </Field>
    </Stack>
  );
};
